import { create } from 'zustand';
import type { TenantBrand } from '@tendedloop/shared';
import { contrastRatio, ensureContrast, mixColor } from '@tendedloop/shared/brand-colors';
import { api, fetchPublicBrand, fetchTokenBrand, getToken, type Office } from '@/lib/api';
import {
  ABSENT,
  FAILED,
  PENDING,
  captureHint,
  lookOf,
  pickBrand,
  ready,
  sessionSource,
  setHint,
  validHandle,
  type Source,
} from '@/lib/brandSources';

/**
 * The building's brand on this device (precedence and sources: brandSources.ts).
 * Without a brand the portal keeps TendedLoop's look.
 *
 * Only the signed-in resident's own office brand is kept on the device
 * (localStorage, one entry per org handle, cleared at sign-out). A brand
 * fetched for a link's token or `?o=` hint is never stored.
 */

/** Which org's brand the device keeps (the handle), and the brand per handle. */
export const CACHE_KEY = 'portal_brand_v2';
const cacheKeyOf = (handle: string) => `${CACHE_KEY}:${handle}`;
/** The unkeyed cache of earlier versions: removed on start. */
const LEGACY_CACHE_KEY = 'portal_brand_v1';

export function readSessionBrand(): { handle: string; brand: TenantBrand } | null {
  try {
    const handle = validHandle(localStorage.getItem(CACHE_KEY));
    if (!handle) return null;
    const raw = localStorage.getItem(cacheKeyOf(handle));
    const brand = raw ? lookOf(JSON.parse(raw)) : null;
    return brand ? { handle, brand } : null;
  } catch {
    return null; // storage unavailable or corrupt: no brand, not an error
  }
}

/** Keep the signed-in resident's office brand (null forgets every kept brand). */
export function writeSessionBrand(handle: string | null, brand: TenantBrand | null) {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k === CACHE_KEY || k.startsWith(`${CACHE_KEY}:`) || k === LEGACY_CACHE_KEY) localStorage.removeItem(k);
    }
    const h = validHandle(handle);
    if (h && brand) {
      localStorage.setItem(cacheKeyOf(h), JSON.stringify(brand));
      localStorage.setItem(CACHE_KEY, h);
    }
  } catch {
    /* storage unavailable: the brand still applies for this visit */
  }
}

const triplet = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};
const DEFAULT_VARS = [
  '--brand-50',
  '--brand-100',
  '--brand-200',
  '--brand-300',
  '--brand-500',
  '--brand-600',
  '--brand-700',
  '--brand-900',
  '--done',
  '--highlight',
];

/**
 * Sets the portal palette from the brand. Every text use is checked against
 * white: a light brand colour (teal, yellow) is darkened until it reads at
 * WCAG AA, so a brand can never make a button or a check mark unreadable.
 */
export function applyBrand(brand: TenantBrand | null) {
  const root = document.documentElement.style;
  if (!brand?.primary) {
    for (const v of DEFAULT_VARS) root.removeProperty(v);
    if (brand?.done) root.setProperty('--done', triplet(ensureContrast(brand.done, 3)));
    if (brand?.highlight) root.setProperty('--highlight', triplet(brand.highlight));
    return;
  }
  const action = ensureContrast(brand.primary); // white text on it, 4.5:1
  const vars: Record<string, string> = {
    '--brand-50': mixColor(brand.primary, 0.93),
    '--brand-100': mixColor(brand.primary, 0.85),
    '--brand-200': mixColor(brand.primary, 0.7),
    '--brand-300': mixColor(brand.primary, 0.5),
    '--brand-500': action,
    '--brand-600': mixColor(action, -0.12),
    '--brand-700': ensureContrast(mixColor(action, -0.2), 7),
    // Headings: the brand's hue, almost black.
    '--brand-900': mixColor(brand.primary, -0.82),
    // A check mark is a graphic: 3:1 is the bar (WCAG 1.4.11).
    '--done': brand.done ? ensureContrast(brand.done, 3) : action,
    '--highlight': brand.highlight ?? mixColor(brand.primary, 0.85),
  };
  for (const [k, v] of Object.entries(vars)) root.setProperty(k, triplet(v));
}

/** Text colour on the highlight fill: black unless the fill is dark. */
export function onHighlight(brand: TenantBrand | null): string {
  const h = brand?.highlight;
  return h && contrastRatio(h, '#000000') < 4.5 ? '#ffffff' : '#000000';
}

interface BrandState {
  /** The brand on screen (null: TendedLoop's look). */
  brand: TenantBrand | null;
  /** The deciding source has not answered yet: signed-out screens show a skeleton band. */
  loading: boolean;
  /** The org handle of the brand on screen, when known (the home-screen app). */
  handle: string | null;
  /** Where the brand on screen came from. */
  from: 'token' | 'session' | 'hint' | 'preview' | null;
  office: Office | null;
  /** True once this session's office details arrived (the cached brand may be another visit's). */
  loaded: boolean;
  token: Source;
  session: Source;
  hint: Source;
  /** The org hint in force (`?o=`, this browser session). */
  hintHandle: string | null;
  /** Load the office details (and with them the brand) for a signed-in resident. */
  load: () => Promise<void>;
}

/** Recompute the brand on screen from the sources; repaint when it changed. */
function settle(patch: Partial<BrandState> = {}) {
  const next = { ...useBrand.getState(), ...patch };
  if (next.from === 'preview') return useBrand.setState(patch);
  const picked = pickBrand(next);
  const changed = JSON.stringify(picked.brand) !== JSON.stringify(useBrand.getState().brand);
  useBrand.setState({ ...patch, ...picked });
  if (changed) applyBrand(picked.brand);
}

function sessionFromCache(officePending = false): Source {
  return sessionSource({
    signedIn: !!getToken(),
    cached: readSessionBrand(),
    hintHandle: useBrand.getState().hintHandle,
    officePending,
  });
}

/** Sign-out on this device: forget the building's look and go back to TendedLoop's. */
export function forgetBrand() {
  writeSessionBrand(null, null);
  setHint(null);
  settle({ office: null, loaded: false, session: ABSENT, hint: ABSENT, hintHandle: null, token: ABSENT });
}

let hintRun = 0;
/**
 * Take the org hint from this URL (or this session's earlier one) and fetch
 * its public brand. Follows one `{redirect}` to an org's current handle.
 */
export async function startHint(search: string): Promise<void> {
  const handle = captureHint(search);
  if (!handle) return;
  if (handle === useBrand.getState().hintHandle && useBrand.getState().hint.state !== 'failed') return;
  const run = ++hintRun;
  settle({ hintHandle: handle, hint: PENDING });
  // Until this visit's office details answer, the device's kept brand stands
  // for the session (and yields to a link naming another org when signed out).
  // Once they answered, they decide.
  if (!useBrand.getState().loaded) {
    settle({ session: sessionFromCache(useBrand.getState().session.state === 'pending') });
  }
  let answer = await fetchPublicBrand(handle);
  let current = handle;
  if (answer && 'redirect' in answer) {
    current = validHandle(answer.redirect) ?? handle;
    answer = current !== handle ? await fetchPublicBrand(current) : null;
    if (answer && 'redirect' in answer) answer = null; // one redirect only
  }
  if (run !== hintRun) return;
  if (current !== handle) setHint(current);
  if (answer === undefined) return settle({ hint: FAILED });
  const brand = answer && 'brand' in answer ? lookOf(answer.brand) : null;
  settle({ hintHandle: current, hint: ready(brand, current) });
}

/** A sign-in link is in the URL: its office's brand decides once known. */
export function tokenPending() {
  settle({ token: PENDING });
}
/** The link did not sign in: show its office's brand (valid or expired link), never stored. */
export async function loadTokenBrand(token: string): Promise<void> {
  settle({ token: PENDING });
  const answer = await fetchTokenBrand(token);
  if (useBrand.getState().token.state !== 'pending') return; // left the page meanwhile
  settle({ token: answer === undefined ? FAILED : ready(lookOf(answer), null) });
}
export function clearToken() {
  settle({ token: ABSENT });
}

/** Show a brand handed over by the dashboard's preview (never stored, no sources). */
export function previewBrand(brand: TenantBrand | null) {
  useBrand.setState({ brand, loading: false, from: 'preview', handle: null, loaded: true });
  applyBrand(brand);
}

const isPreview = typeof window !== 'undefined' && window.location.pathname === '/preview';
const initial = isPreview ? null : readSessionBrand();
if (!isPreview) {
  try {
    localStorage.removeItem(LEGACY_CACHE_KEY);
  } catch {
    /* storage unavailable */
  }
  applyBrand(initial?.brand ?? null);
}

export const useBrand = create<BrandState>(() => ({
  brand: initial?.brand ?? null,
  loading: false,
  handle: initial?.handle ?? null,
  from: initial ? 'session' : null,
  office: null,
  loaded: false,
  token: ABSENT,
  session: initial ? ready(initial.brand, initial.handle) : ABSENT,
  hint: ABSENT,
  hintHandle: null,
  load: async () => {
    useBrand.setState({ loaded: false });
    // A cached brand of this resident shows meanwhile; without one, the office decides.
    const cached = readSessionBrand();
    settle({ session: cached ? ready(cached.brand, cached.handle) : PENDING });
    try {
      const office = await api.office();
      const brand = lookOf(office.brand);
      const handle = validHandle(office.brand?.handle);
      // Only a brand with a handle is kept, under that handle.
      writeSessionBrand(handle, brand);
      settle({ office, loaded: true, session: ready(brand, handle) });
    } catch {
      // Optional: no office details means no office card, and the cached brand stays.
      settle({ office: null, session: cached ? ready(cached.brand, cached.handle) : FAILED });
    }
  },
}));
